import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TruckIcon,
  MagnifyingGlassIcon,
  StarIcon,
  MapPinIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  PlusIcon,
  ArrowLeftIcon,
  FunnelIcon,
  PencilIcon,
  TrashIcon,
  ShieldExclamationIcon,
  LockOpenIcon,
  ArrowUpTrayIcon,
  EyeIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { adminAPI, User } from '../../services/api';
import { usePaginatedTable } from '../../hooks/usePaginatedTable';
import { useUserDeletionImpact } from '../../hooks/useUserDeletionImpact';
import { useCurrency } from '../../contexts/CurrencyContext';
import Pagination from '../Common/Pagination';
import SuspendUserModal from './SuspendUserModal';
import AddUserModal from './AddUserModal';
import BulkUploadModal from './BulkUploadModal';

interface Dispatcher extends User {
  vehicle_type?: string;
  total_deliveries?: number;
  average_rating?: number;
  total_ratings?: number;
  rider_photo_url?: string;
  is_available?: boolean;
  is_active?: boolean;
  current_latitude?: number;
  current_longitude?: number;
  location_updated_at?: string;
  numeric_id?: number;
  wallet_balance?: number;
  profile_complete?: boolean;
  vehicle?: {
    vehicle_type: string;
    license_plate: string;
    make?: string;
    model?: string;
    year?: number;
    color?: string;
    capacity_kg: number;
    vehicle_photo_url?: string;
    vehicle_photo_front?: string;
    vehicle_photo_back?: string;
    vehicle_photo_side?: string;
    vehicle_registration_doc?: string;
  };
  bank_info?: {
    bank_name: string;
    account_number: string;
    account_name: string;
  };
}

const DispatcherManagement: React.FC = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [dispatchers, setDispatchers] = useState<Dispatcher[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDispatcher, setSelectedDispatcher] = useState<Dispatcher | null>(null);
  const [editingDispatcher, setEditingDispatcher] = useState<Dispatcher | null>(null);
  const [deletingDispatcher, setDeletingDispatcher] = useState<Dispatcher | null>(null);
  const [suspendingDispatcher, setSuspendingDispatcher] = useState<Dispatcher | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email_verified: false,
    phone_verified: false
  });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const filteredDispatchers = dispatchers.filter(dispatcher => {
    const fullName = `${dispatcher.first_name || ''} ${dispatcher.last_name || ''}`.toLowerCase();
    const normalizedSearch = searchTerm.toLowerCase().replace(/[\s\-()]/g, '');
    const normalizedPhone = dispatcher.phone?.replace(/[\s\-()]/g, '') || '';
    
    const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || 
                         dispatcher.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         normalizedPhone.includes(normalizedSearch);
    
    let matchesStatus = true;
    if (statusFilter === 'active') {
      matchesStatus = dispatcher.account_status === 'active' && dispatcher.is_active === true;
    } else if (statusFilter === 'suspended') {
      matchesStatus = dispatcher.account_status === 'suspended';
    } else if (statusFilter === 'available') {
      matchesStatus = dispatcher.is_available === true;
    } else if (statusFilter === 'busy') {
      matchesStatus = dispatcher.is_available === false;
    }
    
    return matchesSearch && matchesStatus;
  });

  const {
    paginatedItems,
    currentPage,
    totalPages,
    itemsPerPage,
    totalItems,
    goToPage,
    setItemsPerPage,
    resetPagination
  } = usePaginatedTable({
    items: filteredDispatchers,
    initialItemsPerPage: 10
  });

  const {
    dependencies,
    loading: impactLoading,
    error: impactError,
    fetchDependencies,
    reset: resetImpact
  } = useUserDeletionImpact();

  useEffect(() => {
    fetchDispatchers();
  }, []);

  useEffect(() => {
    resetPagination();
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (deletingDispatcher && deletingDispatcher.numeric_id) {
      fetchDependencies(deletingDispatcher.numeric_id);
    } else {
      resetImpact();
    }
  }, [deletingDispatcher]);

  const fetchDispatchers = async () => {
    try {
      const allUsers = await adminAPI.getAllUsers();
      const riderUsers = allUsers
        .filter(user => user.role === 'rider' && user.approval_status === 'approved')
        .map((user: any) => ({
          ...user,
          numeric_id: typeof user.numeric_id === 'number' ? user.numeric_id : parseInt(user.id) || 0,
        }));
      setDispatchers(riderUsers);
    } catch (error) {
      console.error('Error fetching dispatchers:', error);
      showNotification('error', 'Failed to fetch dispatchers');
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleEditDispatcher = (dispatcher: Dispatcher) => {
    setEditingDispatcher(dispatcher);
    setEditForm({
      first_name: dispatcher.first_name || '',
      last_name: dispatcher.last_name || '',
      phone: dispatcher.phone || '',
      email_verified: dispatcher.is_verified || false,
      phone_verified: false
    });
  };

  const handleSaveEdit = async () => {
    if (!editingDispatcher) return;
    
    try {
      await adminAPI.updateUser(editingDispatcher.numeric_id!, editForm);
      showNotification('success', 'Dispatcher updated successfully');
      setEditingDispatcher(null);
      fetchDispatchers();
    } catch (error: any) {
      showNotification('error', error.response?.data?.detail || 'Failed to update dispatcher');
    }
  };

  const handleDeleteDispatcher = async () => {
    if (!deletingDispatcher) return;
    
    try {
      await adminAPI.deleteUser(deletingDispatcher.numeric_id!);
      showNotification('success', 'Dispatcher deleted successfully');
      setDeletingDispatcher(null);
      fetchDispatchers();
    } catch (error: any) {
      showNotification('error', error.response?.data?.detail || 'Failed to delete dispatcher');
    }
  };

  const handleUnsuspendDispatcher = async (dispatcher: Dispatcher) => {
    try {
      await adminAPI.unsuspendUser(dispatcher.numeric_id!, { note: 'Reinstated by admin', notify_user: true });
      showNotification('success', 'Dispatcher unsuspended successfully');
      fetchDispatchers();
    } catch (error: any) {
      showNotification('error', error.response?.data?.detail || 'Failed to unsuspend dispatcher');
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          star <= Math.round(rating) ? (
            <StarIconSolid key={star} className="h-4 w-4 text-yellow-500" />
          ) : (
            <StarIcon key={star} className="h-4 w-4 text-gray-300" />
          )
        ))}
      </div>
    );
  };

  const getStatusBadge = (dispatcher: Dispatcher) => {
    if (dispatcher.account_status === 'suspended') {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Suspended</span>;
    }
    if (dispatcher.is_available) {
      return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Available</span>;
    }
    return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Busy</span>;
  };

  const totalDispatchers = dispatchers.length;
  const availableDispatchers = dispatchers.filter(d => d.is_available).length;
  const activeDispatchers = dispatchers.filter(d => d.is_active && d.account_status !== 'suspended').length;
  const avgRating = dispatchers.length > 0 
    ? (dispatchers.reduce((sum, d) => sum + (d.average_rating || 0), 0) / dispatchers.length)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-volexx-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-poppins">Loading dispatchers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin-dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 font-poppins"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 font-poppins">Dispatcher Management</h1>
              <p className="mt-2 text-gray-600 font-poppins">View and manage all platform dispatchers</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBulkUploadModal(true)}
                className="inline-flex items-center px-4 py-2 border border-volexx-blue-600 text-volexx-blue-600 rounded-volexx font-semibold font-poppins hover:bg-volexx-blue-50 transition-colors"
              >
                <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                Bulk Upload
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 bg-volexx-blue-600 text-white rounded-volexx font-semibold font-poppins hover:bg-volexx-blue-700 transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Dispatcher
              </button>
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className={`mb-6 p-4 rounded-volexx-lg ${notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            <div className="flex items-center">
              {notification.type === 'success' ? (
                <CheckCircleIcon className="h-5 w-5 mr-2" />
              ) : (
                <XCircleIcon className="h-5 w-5 mr-2" />
              )}
              <span className="font-poppins">{notification.message}</span>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Total Dispatchers</p>
                <p className="mt-2 text-3xl font-bold text-volexx-blue-600 font-poppins">{totalDispatchers}</p>
              </div>
              <div className="bg-volexx-blue-100 rounded-volexx p-3">
                <UserGroupIcon className="h-8 w-8 text-volexx-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Available Now</p>
                <p className="mt-2 text-3xl font-bold text-green-600 font-poppins">{availableDispatchers}</p>
              </div>
              <div className="bg-green-100 rounded-volexx p-3">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Active Accounts</p>
                <p className="mt-2 text-3xl font-bold text-purple-600 font-poppins">{activeDispatchers}</p>
              </div>
              <div className="bg-purple-100 rounded-volexx p-3">
                <TruckIcon className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 font-poppins">Average Rating</p>
                <p className="mt-2 text-3xl font-bold text-orange-600 font-poppins">{avgRating.toFixed(1)}</p>
              </div>
              <div className="bg-orange-100 rounded-volexx p-3">
                <StarIconSolid className="h-8 w-8 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
              />
            </div>
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-volexx border border-gray-300 px-4 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="available">Available</option>
                <option value="busy">Busy</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
          </div>
        </div>

        {/* Dispatchers Table */}
        <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 font-poppins">
              Dispatchers ({totalItems})
            </h2>
          </div>
          
          {paginatedItems.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <TruckIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-gray-900 font-poppins mb-2">No dispatchers found</h3>
              <p className="text-gray-600 font-poppins">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Dispatcher
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Contact
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Vehicle
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Rating
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Wallet
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedItems.map((dispatcher) => (
                      <tr key={dispatcher.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              {dispatcher.rider_photo_url ? (
                                <img
                                  src={dispatcher.rider_photo_url}
                                  alt={`${dispatcher.first_name} ${dispatcher.last_name}`}
                                  className="h-10 w-10 rounded-full object-cover border-2 border-gray-200"
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    (e.currentTarget.nextElementSibling as HTMLElement)?.classList.remove('hidden');
                                  }}
                                />
                              ) : null}
                              <div className={`h-10 w-10 rounded-full bg-volexx-blue-100 flex items-center justify-center ${dispatcher.rider_photo_url ? 'hidden' : ''}`}>
                                <TruckIcon className="h-5 w-5 text-volexx-blue-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-bold text-gray-900 font-poppins">
                                {dispatcher.first_name} {dispatcher.last_name}
                              </div>
                              <div className="text-xs text-gray-500 font-poppins">ID: {dispatcher.numeric_id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            <div className="flex items-center text-sm text-gray-900 font-poppins">
                              <EnvelopeIcon className="h-4 w-4 mr-1 text-gray-400" />
                              <span className="truncate max-w-xs">{dispatcher.email}</span>
                            </div>
                            <div className="flex items-center text-sm text-gray-500 font-poppins">
                              <PhoneIcon className="h-4 w-4 mr-1 text-gray-400" />
                              {dispatcher.phone}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {dispatcher.vehicle ? (
                            <div>
                              <div className="text-sm font-semibold text-gray-900 font-poppins capitalize">
                                {dispatcher.vehicle.vehicle_type}
                              </div>
                              {dispatcher.vehicle.license_plate && (
                                <div className="text-xs text-gray-600 font-poppins font-mono">
                                  {dispatcher.vehicle.license_plate}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 font-poppins">No vehicle</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {renderStars(dispatcher.average_rating || 0)}
                            <span className="text-sm font-semibold text-gray-900 font-poppins">
                              {(dispatcher.average_rating || 0).toFixed(1)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 font-poppins">
                            ({dispatcher.total_ratings || 0} reviews)
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(dispatcher)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-semibold text-gray-900 font-poppins">
                            {formatPrice(dispatcher.wallet_balance || 0)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setSelectedDispatcher(dispatcher)}
                              className="p-2 text-volexx-blue-600 hover:bg-volexx-blue-50 rounded-volexx transition-colors"
                              title="View Details"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => navigate(`/admin/communication?dispatcher=${dispatcher.id}`)}
                              className="p-2 text-orange-600 hover:bg-orange-50 rounded-volexx transition-colors"
                              title="Message Dispatcher"
                            >
                              <ChatBubbleLeftRightIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleEditDispatcher(dispatcher)}
                              className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-volexx transition-colors"
                              title="Edit"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            {dispatcher.account_status === 'suspended' ? (
                              <button
                                onClick={() => handleUnsuspendDispatcher(dispatcher)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-volexx transition-colors"
                                title="Unsuspend"
                              >
                                <LockOpenIcon className="h-5 w-5" />
                              </button>
                            ) : (
                              <button
                                onClick={() => setSuspendingDispatcher(dispatcher)}
                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-volexx transition-colors"
                                title="Suspend"
                              >
                                <ShieldExclamationIcon className="h-5 w-5" />
                              </button>
                            )}
                            <button
                              onClick={() => setDeletingDispatcher(dispatcher)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-volexx transition-colors"
                              title="Delete"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                onPageChange={goToPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </>
          )}
        </div>
      </div>

      {/* Dispatcher Details Modal */}
      {selectedDispatcher && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setSelectedDispatcher(null)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-volexx-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 font-poppins">Dispatcher Details</h3>
                  <button onClick={() => setSelectedDispatcher(null)} className="text-gray-400 hover:text-gray-500">
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Profile Section */}
                <div className="flex items-start gap-6 mb-6">
                  <div className="flex-shrink-0">
                    {selectedDispatcher.rider_photo_url ? (
                      <img
                        src={selectedDispatcher.rider_photo_url}
                        alt={`${selectedDispatcher.first_name} ${selectedDispatcher.last_name}`}
                        className="h-20 w-20 rounded-full object-cover border-4 border-gray-200"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-volexx-blue-100 flex items-center justify-center">
                        <TruckIcon className="h-10 w-10 text-volexx-blue-600" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 font-poppins">
                      {selectedDispatcher.first_name} {selectedDispatcher.last_name}
                    </h4>
                    <p className="text-sm text-gray-600 font-poppins">ID: {selectedDispatcher.numeric_id}</p>
                    <div className="flex items-center gap-2 mt-2">
                      {renderStars(selectedDispatcher.average_rating || 0)}
                      <span className="text-sm font-semibold text-gray-900">
                        {(selectedDispatcher.average_rating || 0).toFixed(1)} ({selectedDispatcher.total_ratings || 0} reviews)
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-volexx p-3">
                    <p className="text-xs text-gray-500 font-poppins">Email</p>
                    <p className="font-semibold text-gray-900 font-poppins text-sm">{selectedDispatcher.email}</p>
                  </div>
                  <div className="bg-gray-50 rounded-volexx p-3">
                    <p className="text-xs text-gray-500 font-poppins">Phone</p>
                    <p className="font-semibold text-gray-900 font-poppins">{selectedDispatcher.phone}</p>
                  </div>
                  <div className="bg-gray-50 rounded-volexx p-3">
                    <p className="text-xs text-gray-500 font-poppins">Wallet Balance</p>
                    <p className="font-semibold text-green-600 font-poppins">{formatPrice(selectedDispatcher.wallet_balance || 0)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-volexx p-3">
                    <p className="text-xs text-gray-500 font-poppins">Status</p>
                    {getStatusBadge(selectedDispatcher)}
                  </div>
                  <div className="bg-gray-50 rounded-volexx p-3">
                    <p className="text-xs text-gray-500 font-poppins">Profile Status</p>
                    <p className={`font-semibold font-poppins ${selectedDispatcher.profile_complete === false ? 'text-orange-600' : 'text-green-600'}`}>
                      {selectedDispatcher.profile_complete === false ? 'Incomplete' : 'Complete'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-volexx p-3">
                    <p className="text-xs text-gray-500 font-poppins">Member Since</p>
                    <p className="font-semibold text-gray-900 font-poppins">{selectedDispatcher.created_at ? new Date(selectedDispatcher.created_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  {selectedDispatcher.current_latitude && selectedDispatcher.current_longitude && (
                    <div className="bg-gray-50 rounded-volexx p-3">
                      <p className="text-xs text-gray-500 font-poppins">Current Location</p>
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 text-green-500 mr-1" />
                        <span className="text-sm font-semibold text-gray-900 font-poppins">
                          {selectedDispatcher.current_latitude.toFixed(4)}, {selectedDispatcher.current_longitude.toFixed(4)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Vehicle Information Section */}
                {selectedDispatcher.vehicle && (
                  <div className="mt-6 border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-bold text-gray-900 font-poppins mb-4 flex items-center">
                      <TruckIcon className="h-5 w-5 mr-2 text-volexx-blue-600" />
                      Vehicle Information
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-volexx p-3">
                        <p className="text-xs text-gray-500 font-poppins">Type</p>
                        <p className="font-semibold text-gray-900 font-poppins capitalize">{selectedDispatcher.vehicle.vehicle_type}</p>
                      </div>
                      <div className="bg-gray-50 rounded-volexx p-3">
                        <p className="text-xs text-gray-500 font-poppins">License Plate</p>
                        <p className="font-semibold text-gray-900 font-poppins font-mono">{selectedDispatcher.vehicle.license_plate || 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-volexx p-3">
                        <p className="text-xs text-gray-500 font-poppins">Make</p>
                        <p className="font-semibold text-gray-900 font-poppins">{selectedDispatcher.vehicle.make || 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-volexx p-3">
                        <p className="text-xs text-gray-500 font-poppins">Model</p>
                        <p className="font-semibold text-gray-900 font-poppins">{selectedDispatcher.vehicle.model || 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-volexx p-3">
                        <p className="text-xs text-gray-500 font-poppins">Year</p>
                        <p className="font-semibold text-gray-900 font-poppins">{selectedDispatcher.vehicle.year || 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-volexx p-3">
                        <p className="text-xs text-gray-500 font-poppins">Color</p>
                        <p className="font-semibold text-gray-900 font-poppins capitalize">{selectedDispatcher.vehicle.color || 'N/A'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-volexx p-3">
                        <p className="text-xs text-gray-500 font-poppins">Capacity</p>
                        <p className="font-semibold text-gray-900 font-poppins">{selectedDispatcher.vehicle.capacity_kg} kg</p>
                      </div>
                    </div>
                    
                    {/* Vehicle Photos */}
                    {(selectedDispatcher.vehicle.vehicle_photo_front || selectedDispatcher.vehicle.vehicle_photo_back || selectedDispatcher.vehicle.vehicle_photo_side || selectedDispatcher.vehicle.vehicle_registration_doc) && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 font-poppins mb-3">Vehicle Photos</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {selectedDispatcher.vehicle.vehicle_photo_front && (
                            <div className="relative group">
                              <img
                                src={selectedDispatcher.vehicle.vehicle_photo_front}
                                alt="Vehicle Front"
                                className="w-full h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90"
                                onClick={() => window.open(selectedDispatcher.vehicle!.vehicle_photo_front, '_blank')}
                              />
                              <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center py-1 rounded-b-lg">Front</span>
                            </div>
                          )}
                          {selectedDispatcher.vehicle.vehicle_photo_back && (
                            <div className="relative group">
                              <img
                                src={selectedDispatcher.vehicle.vehicle_photo_back}
                                alt="Vehicle Back"
                                className="w-full h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90"
                                onClick={() => window.open(selectedDispatcher.vehicle!.vehicle_photo_back, '_blank')}
                              />
                              <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center py-1 rounded-b-lg">Back</span>
                            </div>
                          )}
                          {selectedDispatcher.vehicle.vehicle_photo_side && (
                            <div className="relative group">
                              <img
                                src={selectedDispatcher.vehicle.vehicle_photo_side}
                                alt="Vehicle Side"
                                className="w-full h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90"
                                onClick={() => window.open(selectedDispatcher.vehicle!.vehicle_photo_side, '_blank')}
                              />
                              <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center py-1 rounded-b-lg">Side</span>
                            </div>
                          )}
                          {selectedDispatcher.vehicle.vehicle_registration_doc && (
                            <div className="relative group">
                              <img
                                src={selectedDispatcher.vehicle.vehicle_registration_doc}
                                alt="Registration"
                                className="w-full h-24 object-cover rounded-lg border border-gray-200 cursor-pointer hover:opacity-90"
                                onClick={() => window.open(selectedDispatcher.vehicle!.vehicle_registration_doc, '_blank')}
                              />
                              <span className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs text-center py-1 rounded-b-lg">Registration</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Bank Information Section */}
                {selectedDispatcher.bank_info && (
                  <div className="mt-6 border-t border-gray-200 pt-6">
                    <h4 className="text-lg font-bold text-gray-900 font-poppins mb-4 flex items-center">
                      <svg className="h-5 w-5 mr-2 text-volexx-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      Bank Information
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-volexx p-3">
                        <p className="text-xs text-gray-500 font-poppins">Bank Name</p>
                        <p className="font-semibold text-gray-900 font-poppins">{selectedDispatcher.bank_info.bank_name}</p>
                      </div>
                      <div className="bg-gray-50 rounded-volexx p-3">
                        <p className="text-xs text-gray-500 font-poppins">Account Number</p>
                        <p className="font-semibold text-gray-900 font-poppins font-mono">{selectedDispatcher.bank_info.account_number}</p>
                      </div>
                      <div className="bg-gray-50 rounded-volexx p-3">
                        <p className="text-xs text-gray-500 font-poppins">Account Name</p>
                        <p className="font-semibold text-gray-900 font-poppins">{selectedDispatcher.bank_info.account_name}</p>
                      </div>
                    </div>
                  </div>
                )}

                {selectedDispatcher.account_status === 'suspended' && (
                  <div className="bg-red-50 border border-red-200 rounded-volexx p-4 mt-4">
                    <p className="text-sm font-semibold text-red-800 font-poppins">Suspension Details</p>
                    <p className="text-sm text-red-600 font-poppins mt-1">
                      Reason: {selectedDispatcher.suspension_reason || 'Not specified'}
                    </p>
                  </div>
                )}
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setSelectedDispatcher(null)}
                  className="w-full inline-flex justify-center rounded-volexx border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm font-poppins"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dispatcher Modal */}
      {editingDispatcher && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setEditingDispatcher(null)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-volexx-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-xl font-bold text-gray-900 font-poppins mb-6">Edit Dispatcher</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 font-poppins mb-1">First Name</label>
                    <input
                      type="text"
                      value={editForm.first_name}
                      onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                      className="w-full rounded-volexx border border-gray-300 px-3 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 font-poppins mb-1">Last Name</label>
                    <input
                      type="text"
                      value={editForm.last_name}
                      onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                      className="w-full rounded-volexx border border-gray-300 px-3 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 font-poppins mb-1">Phone</label>
                    <input
                      type="text"
                      value={editForm.phone}
                      onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                      className="w-full rounded-volexx border border-gray-300 px-3 py-2 font-poppins focus:outline-none focus:ring-2 focus:ring-volexx-blue-500"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleSaveEdit}
                  className="w-full inline-flex justify-center rounded-volexx border border-transparent shadow-sm px-4 py-2 bg-volexx-blue-600 text-base font-medium text-white hover:bg-volexx-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm font-poppins"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setEditingDispatcher(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-volexx border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm font-poppins"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingDispatcher && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setDeletingDispatcher(null)}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-volexx-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg font-bold text-gray-900 font-poppins">Delete Dispatcher</h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 font-poppins">
                        Are you sure you want to delete {deletingDispatcher.first_name} {deletingDispatcher.last_name}? 
                        This action cannot be undone.
                      </p>
                      {dependencies && (
                        <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-volexx p-3">
                          <p className="text-sm font-semibold text-yellow-800 font-poppins">Impact Analysis:</p>
                          <ul className="text-sm text-yellow-700 mt-1 list-disc list-inside">
                            {dependencies.order_count > 0 && <li>{dependencies.order_count} orders will be affected</li>}
                            {dependencies.wallet_balance > 0 && <li>Wallet balance: {formatPrice(dependencies.wallet_balance)}</li>}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleDeleteDispatcher}
                  className="w-full inline-flex justify-center rounded-volexx border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm font-poppins"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeletingDispatcher(null)}
                  className="mt-3 w-full inline-flex justify-center rounded-volexx border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm font-poppins"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suspend User Modal */}
      {suspendingDispatcher && (
        <SuspendUserModal
          user={{
            id: String(suspendingDispatcher.numeric_id),
            numeric_id: suspendingDispatcher.numeric_id,
            name: `${suspendingDispatcher.first_name || ''} ${suspendingDispatcher.last_name || ''}`.trim(),
            email: suspendingDispatcher.email,
            role: 'rider'
          }}
          onClose={() => setSuspendingDispatcher(null)}
          onConfirm={async (data) => {
            try {
              await adminAPI.suspendUser(suspendingDispatcher.numeric_id!, {
                suspension_type: data.suspension_type,
                reason: data.reason,
                duration_hours: data.duration_hours,
                notify_user: data.notify_user
              });
              setSuspendingDispatcher(null);
              fetchDispatchers();
              showNotification('success', 'Dispatcher suspended successfully');
            } catch (error: any) {
              showNotification('error', error.response?.data?.detail || 'Failed to suspend dispatcher');
            }
          }}
        />
      )}

      {/* Add Dispatcher Modal */}
      {showAddModal && (
        <AddUserModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchDispatchers();
            showNotification('success', 'Dispatcher created successfully');
          }}
          defaultRole="rider"
          title="Add New Dispatcher"
        />
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <BulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onSuccess={() => {
            setShowBulkUploadModal(false);
            fetchDispatchers();
            showNotification('success', 'Bulk upload completed successfully');
          }}
          defaultRole="rider"
        />
      )}
    </div>
  );
};

export default DispatcherManagement;
